var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
import { makeAutoObservable, runInAction } from "mobx";
import { inject } from "tsyringe";
import { t, translate } from "../I18n";
import { Sentry } from "../Sentry";
import { Money } from "../core";
import { perRestaurant } from "../di";
import { NextOrderingTime, OrderingHoursAvailability, Payment, PurchaseOrderAvailabilityService, } from "../ordering";
import { ApplePayServiceProvider, CheckoutService, } from "../services";
import { FieldTestsStorageManager } from "../storageBase";
import { Tracking } from "../tracking";
import { CheckoutTracking } from "./CheckoutTracking";
import { PFulfillmentTime } from "./PFulfillmentTime";
import { POrderDetailsProvider } from "./POrderDetails";
import { PProductError } from "./PProductError";
import { PPurchaseOrder } from "./PPurchaseOrder";
import { PRestaurant } from "./PRestaurant";
import { PTipProvider } from "./PTipProvider";
import { ConsentsFormController, ContactFormController, ContactFormControllerToken, FulfillmentFormController, InvoiceFormController, PaymentFormController, PDeliveryAddressCoordinator, } from "./formControllers";
export const PlaceOrderCallback = "PlaceOrderCallback";
let PCheckout = class PCheckout {
    constructor(purchaseOrder, restaurant, checkoutService, applePayServiceProvider, orderDetailsProvider, fulfillmentFormController, fulfillmentTime, paymentFormController, contactFormController, consentsFormController, invoiceFormController, checkoutTracking, placeOrderCallback, tipProvider, nextOrderingTime, orderingHoursAvailability, payment, purchaseOrderAvailabilityService, deliveryAddressCoordinator) {
        this.purchaseOrder = purchaseOrder;
        this.restaurant = restaurant;
        this.checkoutService = checkoutService;
        this.fulfillmentFormController = fulfillmentFormController;
        this.fulfillmentTime = fulfillmentTime;
        this.paymentFormController = paymentFormController;
        this.contactFormController = contactFormController;
        this.consentsFormController = consentsFormController;
        this.invoiceFormController = invoiceFormController;
        this.checkoutTracking = checkoutTracking;
        this.placeOrderCallback = placeOrderCallback;
        this.nextOrderingTime = nextOrderingTime;
        this.orderingHoursAvailability = orderingHoursAvailability;
        this.payment = payment;
        this.purchaseOrderAvailabilityService = purchaseOrderAvailabilityService;
        this.deliveryAddressCoordinator = deliveryAddressCoordinator;
        this.applePayService = applePayServiceProvider.applePayService;
        this.orderDetails = orderDetailsProvider.orderDetails;
        this.tip = tipProvider.tip;
        this.isSubmitted = false;
        this._isLoading = false;
        this._serverErrors = [];
        makeAutoObservable(this);
    }
    init() {
        var _a;
        (_a = this.tip) === null || _a === void 0 ? void 0 : _a.autoSelect();
        this.checkoutTracking.sendBeginCheckoutEvent();
    }
    get formInvalid() {
        return this.isSubmitted && !this.purchaseOrder.isCheckoutFormValid;
    }
    get isLoading() {
        return this._isLoading;
    }
    get enableMarketingConsents() {
        return this.restaurant.enableMarketingConsents;
    }
    get hasMarketingConsentError() {
        return (this.orderDetails.doesAnyPromotionRequireMarketingConsents &&
            !this.purchaseOrder.isAgreedToMarketingConsents);
    }
    get price() {
        return this.purchaseOrder.price;
    }
    get priceValue() {
        var _a, _b;
        return (_b = (_a = this.purchaseOrder.finalPrice) === null || _a === void 0 ? void 0 : _a.asNumber) !== null && _b !== void 0 ? _b : 0;
    }
    get serverErrors() {
        return this._serverErrors;
    }
    get orderingHoursMessage() {
        if (this.orderingHoursAvailability.isOutOfOrderingHours) {
            return PProductError.outOfOrderingHours(this.nextOrderingTime.nextOrderAvailableAt, this.orderingHoursAvailability.canPreorder);
        }
        return null;
    }
    get error() {
        return this.purchaseOrder.error;
    }
    get fulfillmentMethodsOptions() {
        return this.purchaseOrder.fulfillmentMethodOptions;
    }
    get deliveryPrice() {
        return this.purchaseOrder.deliveryPrice;
    }
    get deliveryPriceInCurrentZone() {
        return this.purchaseOrder.deliveryPriceInCurrentZone;
    }
    get packagingPrice() {
        return this.purchaseOrder.packagingPrice;
    }
    get depositPrice() {
        return this.purchaseOrder.depositPrice;
    }
    get serviceFeePrice() {
        return this.purchaseOrder.serviceFeePrice;
    }
    get serviceFeePercentage() {
        return this.purchaseOrder.serviceFeePercentage;
    }
    get isNoContactDeliveryHiddenUnderDelivery() {
        return (this.restaurant.fulfillmentMethods.includes("Delivery") &&
            this.restaurant.fulfillmentMethods.includes("NoContactDelivery"));
    }
    get orderLines() {
        return this.purchaseOrder.orderLines;
    }
    get paymentMediums() {
        return this.restaurant.paymentMediums.raw;
    }
    get isOnlinePayment() {
        var _a;
        return ((_a = this.payment.medium) === null || _a === void 0 ? void 0 : _a.method) === "Online";
    }
    get contactDetailsLocked() {
        return this.purchaseOrder.contactDetailsLocked;
    }
    get restaurantFranchiseName() {
        return this.restaurant.franchiseName;
    }
    get promotions() {
        return this.orderDetails.promotionInstances(false);
    }
    disableMarketingPromotionInstances() {
        this.orderDetails.disableMarketingPromotionInstances();
    }
    unlockContactDetails() {
        this.purchaseOrder.unlockContactDetails();
    }
    get isPaymentMediumRestored() {
        return this.purchaseOrder.isPaymentRestored;
    }
    get canPlaceOrder() {
        return this.purchaseOrder.canPlaceOrder && !this.hasMarketingConsentError;
    }
    // Used only to show debug info
    get availability() {
        return this.purchaseOrderAvailabilityService.availability;
    }
    async checkoutOnline(appOriginPath) {
        var _a;
        const req = this.purchaseOrder.checkoutData({
            appOriginPath,
            tracking: this.checkoutTracking.checkoutData,
            fieldTestExperiments: (_a = FieldTestsStorageManager.restore()) !== null && _a !== void 0 ? _a : {},
            applePayToken: null,
        });
        return await this.checkoutService.checkout(req);
    }
    async checkoutApplePay(appOriginPath, applePayService) {
        var _a, _b;
        const applePayToken = await applePayService.getToken((_a = this.purchaseOrder.finalPrice) !== null && _a !== void 0 ? _a : Money.zero());
        if (applePayToken === null) {
            return null;
        }
        const req = this.purchaseOrder.checkoutData({
            appOriginPath,
            tracking: this.checkoutTracking.checkoutData,
            fieldTestExperiments: (_b = FieldTestsStorageManager.restore()) !== null && _b !== void 0 ? _b : {},
            applePayToken,
        });
        const res = await this.checkoutService.checkout(req);
        if (res !== null && res.tag === "ApplePayResponseToken") {
            await applePayService.registerResponseToken(res.token);
        }
        return res;
    }
    async checkout(appOriginPath) {
        if (Skubacz.configuration.ios_review_mode &&
            this.applePayService !== null &&
            this.payment.isApplePay) {
            return await this.checkoutApplePay(appOriginPath, this.applePayService);
        }
        return await this.checkoutOnline(appOriginPath);
    }
    async placeOrder(appOriginPath) {
        this.isSubmitted = true;
        this.fulfillmentFormController.submit();
        this.paymentFormController.submit();
        this.contactFormController.submit();
        this.invoiceFormController.submit();
        this.consentsFormController.submit();
        this.fulfillmentTime.touch();
        if (this.isLoading) {
            return false;
        }
        if (!this.canPlaceOrder) {
            Tracking.trackEvent({
                tag: "CheckoutAvailabilityError",
                contents: {
                    ...this.purchaseOrder.checkoutLoggingData,
                    restaurantId: this.restaurant.id.value,
                    totalPrice: this.priceValue,
                    errors: this.purchaseOrderAvailabilityService.availability.errors,
                },
            });
            return false;
        }
        runInAction(() => {
            this._isLoading = true;
        });
        const res = await this.checkout(appOriginPath);
        // Unknown server error (eg. 503, 439 etc.)
        if (res === null) {
            runInAction(() => {
                this._isLoading = false;
                this._serverErrors = [
                    translate(t.orders.checkout.server_error_with_contact, {
                        phoneNumber: this.restaurant.orderPhoneNumbers.join(", "),
                    }),
                ];
            });
            Sentry.logError(new Error("PlaceOrderRequest errors: unknown error"), {
                extra: {
                    errors: ["unknown error"],
                    cartContext: this.purchaseOrder.checkoutLoggingData,
                },
            });
            return false;
        }
        // RPC known error
        if (res.tag === "Errors") {
            runInAction(() => {
                this._isLoading = false;
                this._serverErrors = res.contents;
            });
            Sentry.logError(new Error(`PlaceOrderRequest errors: ${res.contents.join(", ")}`), {
                extra: {
                    errors: res.contents,
                    cartContext: this.purchaseOrder.checkoutLoggingData,
                },
            });
            return false;
        }
        // Success
        this.placeOrderCallback(res);
        return true;
    }
};
PCheckout = __decorate([
    perRestaurant(),
    __param(5, inject(FulfillmentFormController)),
    __param(8, inject(ContactFormControllerToken)),
    __param(12, inject(PlaceOrderCallback)),
    __metadata("design:paramtypes", [typeof (_a = typeof PPurchaseOrder !== "undefined" && PPurchaseOrder) === "function" ? _a : Object, typeof (_b = typeof PRestaurant !== "undefined" && PRestaurant) === "function" ? _b : Object, typeof (_c = typeof CheckoutService !== "undefined" && CheckoutService) === "function" ? _c : Object, typeof (_d = typeof ApplePayServiceProvider !== "undefined" && ApplePayServiceProvider) === "function" ? _d : Object, typeof (_e = typeof POrderDetailsProvider !== "undefined" && POrderDetailsProvider) === "function" ? _e : Object, typeof (_f = typeof FulfillmentFormController !== "undefined" && FulfillmentFormController) === "function" ? _f : Object, typeof (_g = typeof PFulfillmentTime !== "undefined" && PFulfillmentTime) === "function" ? _g : Object, typeof (_h = typeof PaymentFormController !== "undefined" && PaymentFormController) === "function" ? _h : Object, typeof (_j = typeof ContactFormController !== "undefined" && ContactFormController) === "function" ? _j : Object, typeof (_k = typeof ConsentsFormController !== "undefined" && ConsentsFormController) === "function" ? _k : Object, typeof (_l = typeof InvoiceFormController !== "undefined" && InvoiceFormController) === "function" ? _l : Object, typeof (_m = typeof CheckoutTracking !== "undefined" && CheckoutTracking) === "function" ? _m : Object, Function, typeof (_o = typeof PTipProvider !== "undefined" && PTipProvider) === "function" ? _o : Object, typeof (_p = typeof NextOrderingTime !== "undefined" && NextOrderingTime) === "function" ? _p : Object, typeof (_q = typeof OrderingHoursAvailability !== "undefined" && OrderingHoursAvailability) === "function" ? _q : Object, typeof (_r = typeof Payment !== "undefined" && Payment) === "function" ? _r : Object, typeof (_s = typeof PurchaseOrderAvailabilityService !== "undefined" && PurchaseOrderAvailabilityService) === "function" ? _s : Object, typeof (_t = typeof PDeliveryAddressCoordinator !== "undefined" && PDeliveryAddressCoordinator) === "function" ? _t : Object])
], PCheckout);
export { PCheckout };
